import { Vector2, Vector3 } from 'three';

import { isDefined } from '../../../../../../utils/variables';
import { DesignNode } from '../../../../../elements/design/design-node/design-node';
import { LineSegmentNode } from '../../../../../elements/design/design-node/utils/line-segment-node';
import { NodeTraversalFlags } from '../../../../../elements/design/design-node/utils/node-traversal-flags';
import {
  getPointNeighbours,
  PointId,
  PointNode,
} from '../../../../../elements/design/design-node/utils/point-node';
import { getPointIdArray } from './select';

/**
 * Recursively finds all the polygons and lines within the provided collection of line segments.
 *
 * Summary of how the algorithm works:
 *
 * A grid is generated with nodes that contains the information of which points are
 * connected to eachother. Different groups will be generated if any nodes are disconnected from
 * eachother and each group is processed seperately.
 *
 * It tries to find the the outer polygon by taking one of the outer nodes then
 * tracing the outline of the geometry counter-clockwise.
 *
 * The tracing is based on angle, it will check all connected line segments from the current
 * node. And then follow the the segment that has the smallest angle counter-clockwise compared
 * to the previous line segment. (In 2D-space)
 *
 * Finding a polygon:
 * When tracing the outer boundary of the geometry it will eventually encounter a node which it
 * has already encountered before. If that happens a polygon has been identified.
 *
 * Once a polygon has been identified it will be added to the array, and then the remaining
 * line segments will be processed recursively.
 *
 * Finding a line:
 * It is possible that the path does not connect into a polygon. In this case that branch will
 * be marked as a "dead end".
 * Then the branch will be traced back to the last node that had multiple branching paths
 * and then follow the next branch from which either another dead end or a polygon could be found.
 *
 * If all possible branches have been discovered and there is no polygon the longest branch
 * will be used and the remaining line segments will be processed recursively.
 */
export const findOuterPolygonsAndLines = (
  designNode: DesignNode,
  lineSegments: LineSegmentNode[],
): {
  lines: PointNode[][];
  polygons: LineSegmentNode[][];
} => {
  const pointIds = getPointIdArray(lineSegments);
  const includedPoints = new Map<PointId, boolean>();
  pointIds.forEach((id) => {
    includedPoints.set(id, true);
  });
  const lineSegmentFilter = (node: LineSegmentNode) => lineSegments.some((x) => x.id === node.id);

  const polygons: LineSegmentNode[][] = [];
  const lines: PointNode[][] = [];

  // Each group of connected line segments will be processed separately.
  for (const group of getGroups(designNode, pointIds, includedPoints, lineSegmentFilter)) {
    const flags = new NodeTraversalFlags();
    const startNode = getStartNode(group);
    const secondNode = getSecondNode(designNode, startNode, includedPoints, lineSegmentFilter);

    flags.setTraversed(startNode.id);

    const result = getPolygonOrBranchPaths(
      designNode,
      flags,
      startNode,
      secondNode,
      includedPoints,
      lineSegmentFilter,
    );

    const totalUsedSegments: LineSegmentNode[] = [];
    for (const branch of result) {
      // If the start node is not marked as a dead end the result is a polygon, otherwise its a line.
      const usedSegments = getUsedSegments(branch.nodes, lineSegments, branch.isPolygon);

      // Append the result.
      if (branch.isPolygon) {
        polygons.push(usedSegments);
      } else {
        lines.push(branch.nodes);
      }

      totalUsedSegments.push(...usedSegments);
    }

    const remainingSegments = getRemainingSegments(
      designNode,
      lineSegments,
      totalUsedSegments,
      group,
      includedPoints,
      lineSegmentFilter,
    );

    // Generate polygons and lines from any remaining segments recursively.
    if (remainingSegments.length > 0) {
      console.log('There are actually some remaining segments. lets see', remainingSegments.length);
      const nestedResult = findOuterPolygonsAndLines(designNode, remainingSegments);
      lines.push(...nestedResult.lines);
      polygons.push(...nestedResult.polygons);
    }
  }

  return { lines: mergeLines(lines), polygons };
};

/**
 * Traverses the grid until either a polygon or only lines have been found.
 */
const getPolygonOrBranchPaths = (
  designNode: DesignNode,
  flags: NodeTraversalFlags,
  startNode: PointNode,
  secondNode: PointNode,
  pointIdMap: Map<PointId, boolean>,
  lineSegmentFilter: (node: LineSegmentNode) => boolean,
): { isPolygon: boolean; nodes: PointNode[] }[] => {
  // Stack to track current branches.
  const stack: { currentBranch: PointNode[] }[] = [];
  // Stores the final polygon or longest branch.
  const results: PointNode[][] = [];

  // Initialize the stack with the starting branch.
  stack.push({ currentBranch: [startNode, secondNode] });

  while (stack.length > 0) {
    const { currentBranch } = stack.pop()!;
    const previous = currentBranch[currentBranch.length - 2];
    const current = currentBranch[currentBranch.length - 1];
    const next = getNextNode(designNode, flags, current, previous, pointIdMap, lineSegmentFilter);

    flags.setTraversed(current.id);

    if (next) {
      // If the next node has already been traversed, it means a closed polygon is found and we return it.
      if (flags.isTraversed(next.id)) {
        return [
          {
            isPolygon: true,
            nodes: currentBranch.slice(
              currentBranch.findIndex((x) => x.id === next.id),
              currentBranch.length,
            ),
          },
        ];
      }

      // Add the next node to the current branch and push it onto the stack.
      currentBranch.push(next);
      stack.push({ currentBranch });
      continue;
    }

    // If no next node, mark the branch as a dead end.
    // It will return an index indicating where the last branch split occurred.
    const branchingPathIndex = markBranchAsDeadEnd(
      designNode,
      currentBranch,
      flags,
      pointIdMap,
      lineSegmentFilter,
    );

    // If the returned index is 0, it means we are back at the starting point. So we mark that as a dead end too.
    if (branchingPathIndex === 0) {
      flags.setDeadEnd(startNode.id);

      if (results.length === 0) {
        // If there are no other results we can return the line directly.
        return [{ isPolygon: false, nodes: currentBranch }];
      }

      break;
    }

    // Save the result.
    results.push(currentBranch);

    // Push the branching path to the stack so it will be processed next.
    const branchingPath = currentBranch.slice(0, branchingPathIndex + 1);
    stack.push({ currentBranch: branchingPath });
  }

  const processedBranches = splitUpBranches(results);

  return processedBranches.map((nodes) => {
    return { isPolygon: false, nodes };
  });
};

function findCommonPrefixCut(mainBranch: PointNode[], otherBranch: PointNode[]) {
  function recurse(i: number) {
    // If we've reached the end of otherBranch or a mismatch
    if (i >= mainBranch.length || i >= otherBranch.length || mainBranch[i] !== otherBranch[i]) {
      // i is where we stopped matching. The last matched index is i-1.
      // If we ended because otherBranch ended (i == otherBranch.length),
      // we know we had a full match of otherBranch elements:
      // In that case, let's keep two matched elements if possible.
      if (i === otherBranch.length) {
        const cutIndex = Math.max(0, i - 2);
        return otherBranch.slice(cutIndex);
      }

      // Otherwise, use the original logic: keep last matched element only.
      const cutIndex = Math.max(0, i - 1);
      return otherBranch.slice(cutIndex);
    }
    return recurse(i + 1);
  }

  return recurse(0);
}

/**
 * Splits up all the branches into their unique parts prioritizing the longest branch first.
 * For example
 *
 * Input:
 * [0,1,2,3,4]
 * [0,1,2,5,6]
 * [0,7,8]
 *
 * Output:
 * [0,1,2,3,4]
 * [2,5,6]
 * [0,7,8]
 */
function splitUpBranches(results: PointNode[][]) {
  // Find the longest branch
  const longestBranch = results.sort((a, b) => b.length - a.length)[0];

  // Process each other branch
  const processed = results.map((branch) => {
    // Don't modify the longest branch itself
    if (branch === longestBranch) {
      return branch;
    }

    return findCommonPrefixCut(longestBranch, branch);
  });

  return processed;
}

/**
 * Gets the start node. it is always the node with the smallest x value.
 */
const getStartNode = (group: PointNode[]) => {
  return group.reduce((prev, cur) => (cur.vertex.x < prev.vertex.x ? cur : prev), group[0]);
};

/**
 * Finds the second node based on the grid and the start node.
 */
const getSecondNode = (
  designNode: DesignNode,
  startNode: PointNode,
  pointIdMap: Map<PointId, boolean>,
  lineSegmentFilter: (node: LineSegmentNode) => boolean,
) => {
  /**
   * The direction that we will trace towards.
   * Since we want to go counter clockwise the general direction is
   * straight downwards in a 2D-plane.
   */
  const traceDirection = new Vector3(0, -1, 0);
  let closestAngle = Number.MAX_VALUE;

  // Iterate all the neighbouring nodes and find the one with the smallest angle.
  const closestNeighbour = getPointNeighbours(startNode, designNode, {
    pointIdMap,
    lineSegmentFilter,
  }).reduce((prev, cur) => {
    const dir = designNode
      .getPointById(cur)
      .vertex.clone()
      .sub(startNode.vertex)
      .setZ(0)
      .normalize();

    const nextAngle = dir.angleTo(traceDirection);
    if (nextAngle < closestAngle) {
      closestAngle = nextAngle;
      return cur;
    }

    return prev;
  }, 0 as PointId);

  return designNode.getPointById(closestNeighbour);
};

/**
 * Gets the next node that has the smallest angle counter-clockwise compared to the last segment.
 */
const getNextNode = (
  designNode: DesignNode,
  flags: NodeTraversalFlags,
  current: PointNode,
  previous: PointNode,
  pointIdMap: Map<PointId, boolean>,
  lineSegmentFilter: (node: LineSegmentNode) => boolean,
) => {
  /**
   * Previous segment. It is reversed so that the angles may be compared to it correctly.
   */
  const previousSegment = previous.vertex.clone().sub(current.vertex);
  const referenceAngle = new Vector2(previousSegment.x, previousSegment.y).angle();
  let closestAngleCounterClockwise = Number.MAX_VALUE;
  let nextNode: PointNode | undefined;

  for (const neighbour of getPointNeighbours(current, designNode, {
    pointIdMap,
    lineSegmentFilter,
  })) {
    // If the neighbour is the previous point, or if it leads to a dead end we skip it.
    if (neighbour === previous.id || flags.isDeadEnd(neighbour)) {
      continue;
    }

    const node = designNode.getPointById(neighbour);
    const nextSegment = node.vertex.clone().sub(current.vertex);
    const angle = new Vector2(nextSegment.x, nextSegment.y)
      .rotateAround(new Vector2(), -referenceAngle)
      .angle();
    if (angle < closestAngleCounterClockwise) {
      closestAngleCounterClockwise = angle;
      nextNode = node;
    }
  }

  return nextNode;
};

/**
 * Marks a branch as dead. Then seraches all the nodes in it backwards until it finds
 * a node with a branching path.
 *
 * All the nodes searched will also be marked as dead ends if they don't have any
 * branching paths.
 * @returns The index of the last branching path or the start nodes index.
 */
const markBranchAsDeadEnd = (
  designNode: DesignNode,
  branch: PointNode[],
  flags: NodeTraversalFlags,
  pointIdMap: Map<PointId, boolean>,
  lineSegmentFilter: (node: LineSegmentNode) => boolean,
) => {
  flags.setDeadEnd(branch[branch.length - 1].id);

  // Excluding the start node because the starting path should never change.
  let i = branch.length - 2;
  for (i; i > 0; i--) {
    // If there are some branches connected to the node that aren't dead ends in the node then break the loop.
    if (
      getPointNeighbours(branch[i], designNode, { pointIdMap, lineSegmentFilter }).some(
        (x) => !flags.isDeadEnd(x) && x !== branch[i - 1].id,
      )
    ) {
      break;
    }

    // Other nodes with no branches are also marked as dead ends on the way back.
    flags.setDeadEnd(branch[i].id);
  }

  return i;
};

/**
 * Find the used line segments based on the point grid result.
 */
export const getUsedSegments = (
  result: PointNode[],
  lineSegments: LineSegmentNode[],
  isPolygon: boolean,
) => {
  const usedSegments: LineSegmentNode[] = [];
  for (let i = 0; i < (isPolygon ? result.length : result.length - 1); i++) {
    const a = result[i];
    const b = result[(i + 1) % result.length];
    const segment = lineSegments.find((x) => x.values.includes(a.id) && x.values.includes(b.id));

    if (segment) {
      usedSegments.push(segment);
    }
  }

  return usedSegments;
};

/**
 * Finds all the line segments that follows these two criteria.
 * - It is not included in used segments
 * - It belongs to the group
 */
const getRemainingSegments = (
  designNode: DesignNode,
  allSegments: LineSegmentNode[],
  usedSegments: LineSegmentNode[],
  group: PointNode[],
  pointIdMap: Map<PointId, boolean>,
  lineSegmentFilter: (node: LineSegmentNode) => boolean,
) => {
  const isRemainingSegment = (segment: LineSegmentNode) => {
    const segmentIsUsed = usedSegments.some((usedSegment) =>
      usedSegment.values.every((x) => segment.values.includes(x)),
    );

    // The segment should not already have been used.
    if (segmentIsUsed) {
      return false;
    }

    const node = group.find((x) => x.id === segment.values[0]);

    // The segment should be included in the group.
    return isDefined(node)
      ? getPointNeighbours(node, designNode, { pointIdMap, lineSegmentFilter }).includes(
          segment.values[1],
        )
      : false;
  };

  // Filter out the remaining segments.
  return allSegments.filter(isRemainingSegment);
};

/**
 * Checks if the end points of any two lines match up and then merges the lines.
 */
const mergeLines = (lines: PointNode[][]) => {
  if (lines.length === 0) {
    return [];
  }

  const mergedLines: PointNode[][] = [lines[0]];

  const getLineStartAndEndSegments = (line: PointNode[]) => {
    return {
      start: line[0],
      end: line[line.length - 1],
    };
  };

  for (let i = 1; i < lines.length; i++) {
    const next = lines[i];
    const { start: nextStart, end: nextEnd } = getLineStartAndEndSegments(lines[i]);

    let match = false;

    for (const line of mergedLines) {
      const { start, end } = getLineStartAndEndSegments(line);

      if (end.id === nextStart.id) {
        line.push(...next.slice(1));
        match = true;
        break;
      }

      if (start.id === nextEnd.id) {
        line.unshift(...next.slice(0, -1));
        match = true;
        break;
      }

      if (end.id === nextEnd.id) {
        line.push(...next.slice(0, -1).reverse());
        match = true;
        break;
      }

      if (start.id === nextStart.id) {
        line.unshift(...next.slice(1).reverse());
        match = true;
        break;
      }
    }
    if (!match) {
      mergedLines.push(next);
    }
  }

  return mergedLines;
};

/**
 * Get points divided up into groups based on which points are connected to eachother
 * through line segments.
 */
function getGroups(
  designNode: DesignNode,
  pointIds: PointId[],
  pointIdMap: Map<PointId, boolean>,
  lineSegmentFilter: (node: LineSegmentNode) => boolean,
): PointNode[][] {
  const groups: PointNode[][] = [];
  const flags = new NodeTraversalFlags();
  const nodes = pointIds.map((id) => designNode.getPointById(id));

  for (const node of nodes) {
    if (flags.isTraversed(node.id)) {
      continue;
    }

    const group: PointNode[] = [];
    // Stack for iterative DFS.
    const stack: PointNode[] = [node];

    while (stack.length > 0) {
      const current = stack.pop()!;

      if (flags.isTraversed(current.id)) {
        continue;
      }

      group.push(current);
      flags.setTraversed(current.id);

      const neighbours = getPointNeighbours(current, designNode, {
        pointIdMap,
        lineSegmentFilter,
      });

      for (const neighbour of neighbours) {
        const neighbourNode = designNode.getPointById(neighbour);
        if (!flags.isTraversed(neighbourNode.id)) {
          stack.push(neighbourNode);
        }
      }
    }

    groups.push(group);
  }

  return groups;
}
